import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Flex } from "antd";
import moment from "moment";
import UserStatusBadge from "../../Components/UserStatusBadge";
import CustomAvatar from "../../Components/CustomAvatar";

const SearchResultItem = React.memo(({
  Record,
  index,
  indexParent,
  totalMessages,
  searchedDataLength,
  SearchCodeRef,
  jumpToMsg
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // Memoized values for better performance
  const chatName = useMemo(() => {
    if (Record.oppositeUserInfo && !Record.channelInfo) {
      return `Direct Message (With ${Record.oppositeUserInfo.userName})`;
    }
    if (Record.channelInfo) {
      return Record.channelInfo.name;
    }
    return "Unknown Chat";
  }, [Record.oppositeUserInfo, Record.channelInfo]);

  const formattedTime = useMemo(() => 
    moment(Record.createdAt).format("hh:mm A"),
    [Record.createdAt]
  );

  const itemClassName = useMemo(() => {
    if (index === 0 && index !== totalMessages - 1) {
      return "chat-list-item first-item";
    }
    if (index === totalMessages - 1 && indexParent !== searchedDataLength - 1 && totalMessages !== 1) {
      return "chat-list-item last-item";
    }
    if (totalMessages === 1) {
      return "chat-list-item middle-item";
    }
    return "chat-list-item";
  }, [index, totalMessages, indexParent, searchedDataLength]);

  const bodyClassName = useMemo(() => {
    let className = "chat__body";
    
    if (Record?.content?.includes('<code')) {
      className += " copy-code-btn";
    }
    
    if (isOverflowing) {
      className += isExpanded ? " expanded" : " collapsed";
    }
    
    return className;
  }, [Record.content, isOverflowing, isExpanded]);

  const maxHeight = useMemo(() => {
    if (isExpanded) return 'none';
    if (Record?.files?.length > 0 || Record?.forwards?.files?.length > 0) {
      return 'auto';
    }
    return '400px';
  }, [isExpanded, Record.files, Record.forwards]);

  // Check if content is overflowing
  useEffect(() => {
    if (SearchCodeRef?.current) {
      const isContentOverflowing = SearchCodeRef.current.scrollHeight > 200;
      setIsOverflowing(isContentOverflowing);
    }
  }, [Record.content, SearchCodeRef]);

  // Event handlers
  const handleToggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const handleJumpToMessage = useCallback(() => {
    jumpToMsg(Record);
  }, [jumpToMsg, Record]);

  // Render expand/collapse button
  const renderExpandButton = () => {
    if (!isOverflowing) return null;

    return (
      <div className="show-btn-wrapper">
        <span className="show-btn">
          <button
            onClick={handleToggleExpand}
            className={isExpanded ? 'up-arrow' : 'down-arrow'}
            aria-label={isExpanded ? 'Show less content' : 'Show more content'}
          >
            {isExpanded ? (
              <>
                <i className="fa-solid fa-angle-up"></i> Show Less
              </>
            ) : (
              <>
                <i className="fa-solid fa-angle-down"></i> Show More
              </>
            )}
          </button>
        </span>
      </div>
    );
  };

  return (
    <li className={itemClassName} data-index={index}>
      <span className="search-channel_name">
        <span>{chatName}</span>
      </span>
      
      <Flex gap="small" className="chat-container">
        <div className="avatar-wrapper">
          <div className="avatar-badge-wrapper">
            <CustomAvatar
              size={32}
              userName={Record.senderInfo.username}
              img={Record.senderInfo.thumbnailAvatarUrl}
            />
            <UserStatusBadge
              font_size=".75rem"
              badgeBackground="#FFF"
              showStatus={false}
              status={Record.senderInfo.status}
            />
          </div>
        </div>
        
        <Flex vertical className="chat-info-wrapper">
          <Flex gap="middle" align="center" className="chat__header">
            <span className="chat__header--name">
              {Record.senderInfo.userName}
            </span>
            <span className="chat__header--time">
              {formattedTime}
            </span>
            <button 
              onClick={handleJumpToMessage} 
              className="jump-btn"
              aria-label="Jump to message"
            >
              <i className="fa-solid fa-angles-left"></i>
              <span>Jump</span>
            </button>
          </Flex>
          
          <div 
            className={bodyClassName}
            style={{
              maxHeight,
              overflowY: 'hidden',
            }}
          >
            <div
              ref={SearchCodeRef}
              className="chat__body--msg"
              dangerouslySetInnerHTML={{
                __html: Record.content || "",
              }}
            />
          </div>
          
          {renderExpandButton()}
        </Flex>
      </Flex>
    </li>
  );
});

export default SearchResultItem;